import React from 'react';

import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import { Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';

import { ALLOWED_IMAGE_UPLOAD_ACCEPT } from '~/services/ImageService';

import useImageDropUpload from '../useImageDropUpload';
import {
  uploadTileContentTransitionSx,
  uploadTileDragActiveContentSx,
  uploadTileDragActiveSx,
  uploadTileTransitionSx,
} from '../utils';

import SortableRoomImageSlot from './SortableSlot';
import type { RoomImage } from './utils';

interface Props {
  canToggleHidden?: boolean;
  hiddenImagePublicIds: Array<string>;
  image?: RoomImage;
  isMetadataEditable?: boolean;
  selectedImagePublicIds: Array<string>;
  onDeleteImage: (publicId: string) => void;
  onEditImage: (publicId: string) => void;
  onToggleHiddenImage: (publicId: string) => Promise<void>;
  onToggleSelected: (publicId: string) => void;
  onUpdateImageTitle: (publicId: string, title: string) => void;
  onUploadImages?: (files: Array<File>) => void;
  slotNumber: number;
}

export default function ImagesByRoomSlot({
  canToggleHidden,
  hiddenImagePublicIds,
  image,
  isMetadataEditable = false,
  selectedImagePublicIds,
  onDeleteImage,
  onEditImage,
  onToggleHiddenImage,
  onToggleSelected,
  onUpdateImageTitle,
  onUploadImages,
  slotNumber,
}: Props) {
  const slotLabel = slotNumber === 1 ? `#${slotNumber} Hero` : `#${slotNumber}`;
  const { dragDropProps, isDragActive, uploadLabel } = useImageDropUpload(onUploadImages);
  const handleUploadInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);

    event.target.value = '';

    if (files.length === 0) {
      return;
    }

    onUploadImages?.(files);
  };

  if (image) {
    return (
      <Stack
        spacing={isMetadataEditable ? 1 : 0}
        sx={{
          minWidth: 0,
          p: isMetadataEditable ? 1 : 0,
          border: isMetadataEditable ? 1 : 0,
          borderColor: 'divider',
          borderRadius: isMetadataEditable ? 1 : 0,
          bgcolor: isMetadataEditable ? 'background.paper' : 'transparent',
        }}
      >
        <SortableRoomImageSlot
          canToggleHidden={!!canToggleHidden}
          hasFrame={!isMetadataEditable}
          image={image}
          isHidden={hiddenImagePublicIds.includes(image.publicId)}
          isSelected={selectedImagePublicIds.includes(image.publicId)}
          onDeleteImage={onDeleteImage}
          onEditImage={onEditImage}
          onToggleHiddenImage={onToggleHiddenImage}
          onToggleSelected={onToggleSelected}
          slotLabel={slotLabel}
        />
        {isMetadataEditable && (
          <TextField
            fullWidth
            size="small"
            label="Short description"
            value={image.title ?? ''}
            onChange={(event) => onUpdateImageTitle(image.publicId, event.target.value)}
          />
        )}
      </Stack>
    );
  }

  if (isMetadataEditable) {
    return (
      <Button
        component={onUploadImages ? 'label' : 'button'}
        variant="text"
        disabled={!onUploadImages}
        {...dragDropProps}
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 1,
          overflow: 'hidden',
          width: '100%',
          minWidth: 0,
          p: 1,
          border: 1,
          borderStyle: 'dashed',
          borderColor: 'primary.main',
          borderRadius: 1,
          bgcolor: 'action.hover',
          textTransform: 'none',
          ...uploadTileTransitionSx,
          '&:hover': {
            bgcolor: 'action.selected',
          },
          ...(isDragActive ? uploadTileDragActiveSx : undefined),
        }}
      >
        <Box sx={{ width: '100%', aspectRatio: '16 / 9' }} />
        <Box sx={{ height: METADATA_DESCRIPTION_FIELD_HEIGHT }} />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'primary.main',
            ...uploadTileContentTransitionSx,
            ...(isDragActive ? uploadTileDragActiveContentSx : undefined),
          }}
        >
          <Stack alignItems="center" spacing={0.75}>
            <AddPhotoAlternateOutlinedIcon fontSize="large" />
            <Typography variant="body2" fontWeight={700} textAlign="center">
              {uploadLabel}
            </Typography>
          </Stack>
        </Box>
        <Chip label={slotLabel} size="small" sx={roomImageSlotChipSx} />
        {onUploadImages && (
          <input hidden accept={ALLOWED_IMAGE_UPLOAD_ACCEPT} multiple type="file" onChange={handleUploadInputChange} />
        )}
      </Button>
    );
  }

  return (
    <Button
      component={onUploadImages ? 'label' : 'button'}
      variant="text"
      disabled={!onUploadImages}
      {...dragDropProps}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        aspectRatio: '16 / 9',
        alignSelf: 'stretch',
        width: '100%',
        border: 1,
        borderStyle: 'dashed',
        borderColor: 'primary.main',
        borderRadius: 1,
        bgcolor: 'action.hover',
        minWidth: 0,
        p: 0,
        textTransform: 'none',
        ...uploadTileTransitionSx,
        '&:hover': {
          bgcolor: 'action.selected',
        },
        ...(isDragActive ? uploadTileDragActiveSx : undefined),
      }}
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={0.75}
        sx={{
          width: '100%',
          height: '100%',
          color: 'primary.main',
          ...uploadTileContentTransitionSx,
          ...(isDragActive ? uploadTileDragActiveContentSx : undefined),
        }}
      >
        <AddPhotoAlternateOutlinedIcon fontSize="large" />
        <Typography variant="body2" fontWeight={700} textAlign="center">
          {uploadLabel}
        </Typography>
      </Stack>
      <Chip label={slotLabel} size="small" sx={roomImageSlotChipSx} />
      {onUploadImages && (
        <input hidden accept={ALLOWED_IMAGE_UPLOAD_ACCEPT} multiple type="file" onChange={handleUploadInputChange} />
      )}
    </Button>
  );
}

const METADATA_DESCRIPTION_FIELD_HEIGHT = 40;

const roomImageSlotChipSx = {
  position: 'absolute',
  top: 10,
  left: 10,
  bgcolor: 'grey.800',
  color: 'common.white',
  fontWeight: 700,
};
